import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import { SButton, SText } from '@setvi/shared/components';
import { SConfirmDialogV2 } from '@setvi/shared/components/sdialogs';
import { Types } from '@setvi/shared/components/sdialogs/sconfirm/helper';
import { useDialog } from '@setvi/shared/providers';

const DELETE_NOTE_DIALOG_ID = 'delete-customer-note';

interface CustomerNoteDialogProps {
  initialNote?: string;
  onClose: () => void;
  onSave: (note: string) => void;
  onDelete?: () => void;
  // Hide the built-in title/close when rendered inside the tabbed NotesDialog.
  hideHeader?: boolean;
}

const CustomerNoteDialog = ({
  initialNote = '',
  onClose,
  onSave,
  onDelete,
  hideHeader = false
}: CustomerNoteDialogProps) => {
  const [note, setNote] = useState(initialNote);
  const trimmed = note.trim();
  const { openDialog, closeDialog } = useDialog();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Last remote value we've seen. Lets us tell a collaborator's update apart
  // from the user's own local typing.
  const lastRemoteRef = useRef(initialNote);

  // When another collaborator saves, `initialNote` changes (it's read live from
  // the store by the parent). Adopt the incoming value, but only when the user
  // has no unsaved local edits — otherwise we'd discard what they're typing
  // (DEV-471).
  useEffect(() => {
    if (initialNote === lastRemoteRef.current) return;
    setNote(prev =>
      prev.trim() === lastRemoteRef.current.trim() ? initialNote : prev
    );
    lastRemoteRef.current = initialNote;
  }, [initialNote]);

  // Place caret at the end instead of the default position 0 when preloaded text exists.
  const handleInputRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (el && initialNote) {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  };

  const handleSave = () => {
    if (!trimmed) return;
    onSave(trimmed);
    onClose();
  };

  const handleDelete = () => {
    openDialog(
      <SConfirmDialogV2
        type={Types.delete}
        title="Delete Customer Note?"
        description="It will no longer appear in the RFQ export."
        confirmText="Delete Note"
        closeText="Cancel"
        onClose={() => closeDialog(DELETE_NOTE_DIALOG_ID)}
        onConfirm={() => {
          onDelete?.();
          closeDialog(DELETE_NOTE_DIALOG_ID);
          onClose();
        }}
      />,
      { id: DELETE_NOTE_DIALOG_ID }
    );
  };

  return (
    <Box
      style={{
        width: hideHeader ? '100%' : 420,
        padding: 20,
        boxSizing: 'border-box'
      }}>
      {!hideHeader && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8
          }}>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <SText
              size="lg"
              weight="bold"
              fontColor="#20262D"
              title="Customer Note"
            />
            <SText
              size="sm"
              fontColor="#5D7083"
              noLineClamp
              title="Shared with your customer. Appears as a dedicated column in the exported quote."
            />
          </Box>

          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon htmlColor="#5D7083" style={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      <Box style={{ marginTop: hideHeader ? 0 : 16 }}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          autoFocus
          variant="outlined"
          placeholder="Leave a note..."
          value={note}
          onChange={e => setNote(e.target.value)}
          inputRef={handleInputRef}
        />
      </Box>

      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 16
        }}>
        <Box>
          {!!initialNote && (
            <SButton
              variant="text"
              onClick={handleDelete}
              style={{ color: '#FA2020', fontWeight: 600 }}>
              Delete Note
            </SButton>
          )}
        </Box>

        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SButton
            variant="text"
            color="default"
            onClick={onClose}
            style={{ color: '#5D7083' }}>
            Cancel
          </SButton>
          <SButton
            variant="contained"
            color="primary"
            disabled={!trimmed}
            onClick={handleSave}
            style={{ borderRadius: 36, padding: '6px 20px' }}>
            Save
          </SButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerNoteDialog;
